import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import {
  closeDrawerKeyframes,
  openDrawerKeyframes,
  backgroundFadeOut,
  backgroundFadeIn,
  drawerPositions
} from './drawerStyleSettings';
import Portal from '../Portal';
import { IsServerSideRendering } from '../../utilities';

const DrawerModal = styled.div`
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  z-index: 1300;
  position: fixed;
`;

const DrawerBackdrop = styled.div`
  ${props => props.backgroundAnimation}
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 900;
  opacity: 1;
  position: fixed;
  background-color: rgba(0, 0, 0, 0.5);
  -webkit-tap-highlight-color: transparent;
`;

const DrawerWrapper = styled.div`
  ${props => props.drawerAnimation}
  background-color: ${props => props.theme.carvana.gray.background};
  top: 0;
  flex: 1 0 auto;
  height: 100vh;
  display: flex;
  z-index: 1200;
  outline: none;
  position: fixed;
  flex-direction: column;
  -webkit-overflow-scrolling: touch;
  transform: ${props => (props.isOpen ? 'translate3d(0, 0, 0)' : 'translate3d(100vw, 100vh, 0)')};

  ${props => props.drawerPositioning};
  ${props => props.drawerStyles};

  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  text-align: left;
  -webkit-font-smoothing: antialiased;
  box-sizing: border-box;

  a {
    text-decoration: none;
  }

  p {
    margin-top: 0;
  }

  * {
    box-sizing: border-box;
  }
`;

class Drawer extends PureComponent {
  static propTypes = {
    children: PropTypes.node.isRequired,
    isOpen: PropTypes.bool.isRequired,
    anchor: PropTypes.string,
    onClose: PropTypes.func,
    drawerStyles: PropTypes.shape({})
  };

  static defaultProps = {
    anchor: 'left',
    onClose: () => null,
    drawerStyles: {}
  };

  constructor(props) {
    super(props);

    this.state = {
      isAnimating: false
    };

    this.handleAnimationEnd = this.handleAnimationEnd.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    const { isOpen } = this.props;
    if (!nextProps.isOpen && isOpen) {
      this.setState({ isAnimating: true });
    }
  }

  getDrawerAnimation() {
    const { isOpen, anchor } = this.props;
    if (isOpen) {
      return openDrawerKeyframes[anchor];
    }

    return closeDrawerKeyframes[anchor];
  }

  getBackgroundAnimation() {
    const { isOpen } = this.props;
    if (isOpen) {
      return backgroundFadeIn;
    }
    return backgroundFadeOut;
  }

  handleBackgroundScrolling() {
    if (IsServerSideRendering) {
      return;
    }

    const { isOpen } = this.props;
    const { isAnimating } = this.state;
    if (isOpen || isAnimating) {
      document.body.style.overflow = 'hidden';
      return;
    }
    document.body.style.overflow = '';
  }

  handleAnimationEnd() {
    this.setState({ isAnimating: false });
  }

  render() {
    const { anchor, children, isOpen, onClose, drawerStyles } = this.props;
    const { isAnimating } = this.state;
    this.handleBackgroundScrolling();
    if (!isOpen && !isAnimating) {
      return null;
    }

    const backgroundAnimation = css`animation: ${this.getBackgroundAnimation()} 400ms cubic-bezier(0.23, 1, 0.32, 1);`;
    const drawerAnimation = css`animation: ${this.getDrawerAnimation()} 400ms cubic-bezier(0.23, 1, 0.32, 1);`;
    return (
      <Portal>
        <DrawerModal>
          <DrawerBackdrop onClick={onClose} backgroundAnimation={backgroundAnimation} />
          <DrawerWrapper
            onAnimationEnd={this.handleAnimationEnd}
            drawerAnimation={drawerAnimation}
            isOpen={isOpen}
            drawerPositioning={drawerPositions[anchor]}
            drawerStyles={drawerStyles}
          >
            {children}
          </DrawerWrapper>
        </DrawerModal>
      </Portal>
    );
  }
}

export default Drawer;
